import React, { createContext, useContext, useMemo } from 'react';
import { FORM_REGISTRY_BLUEPRINTS } from './formRegistryData';
import { useFormRegistryCollection } from './formRegistryStore';

// Applies Form Registry edits of a Standard form to a module's hand-built Add
// Record page. The page wraps its form in <RegistryFormScope> and its field
// components call useRegistryFieldProps(label, props): the field is found by
// the label it was built with (the code-default label in the registry) and
// whatever the admin changed — label, hint, placeholder, required marker,
// dropdown options — replaces the page's own value. Anything the admin did
// not change keeps the page's own wording, so an untouched form renders
// exactly as before.

const ScopeContext = createContext(null);
const FieldContext = createContext(null);

// Where a page words a field differently from its registry blueprint, the
// page's label(s) — keyed by "<form id>:<field id>".
const PAGE_LABEL_ALIASES = {
  'market-price-observation:price': ['Price (₦)'],
  'livestock-actor-registration:gender': ['Sex'],
  'workforce-deployment:actor-id': ['Livestock Actor'],
  'workforce-deployment:institution-facility': ['Posting Institution'],
  'organisation-cooperative-registration:cooperative-name': ['Organisation Name'],
  'organisation-cooperative-registration:organisation-type': ['Cooperative Type'],
  'conflict-incident-report:economic-loss': ['Estimated Loss (NGN)'],
  'ppp-arrangements-arrangements:ppp-arrangement-id': ['PPP ID'],
};

export function normalizeRegistryLabel(label) {
  return String(label ?? '').toLowerCase().replace(/\*/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

function isCustomType(form) {
  return String(form?.type ?? '').toLowerCase() === 'custom';
}

function inScope(form, moduleKey, submoduleKey) {
  return String(form.moduleKey ?? '') === String(moduleKey ?? '')
    && (!submoduleKey || String(form.submoduleKey ?? '') === String(submoduleKey));
}

function flatFields(form) {
  return (form?.sections ?? []).flatMap((section) => section.fields ?? []);
}

// label (as built into the page) -> { base, current } for every field of the
// scope's Standard form(s), plus the same for section titles.
function buildIndex(forms, moduleKey, submoduleKey) {
  const fields = new Map();
  const sections = new Map();
  forms
    .filter((form) => !isCustomType(form) && inScope(form, moduleKey, submoduleKey))
    .forEach((form) => {
      const base = FORM_REGISTRY_BLUEPRINTS.find((blueprint) => blueprint.id === form.id);
      if (!base) return;
      const currentById = new Map(flatFields(form).map((field) => [field.id, field]));
      flatFields(base).forEach((baseField) => {
        const current = currentById.get(baseField.id);
        if (!current) return;
        [baseField.label, ...(PAGE_LABEL_ALIASES[`${form.id}:${baseField.id}`] ?? [])].forEach((label) => {
          const key = normalizeRegistryLabel(label);
          if (key && !fields.has(key)) fields.set(key, { base: baseField, current });
        });
      });
      const currentSections = new Map((form.sections ?? []).map((section) => [section.id, section]));
      (base.sections ?? []).forEach((baseSection) => {
        const key = normalizeRegistryLabel(baseSection.title);
        const current = currentSections.get(baseSection.id);
        if (key && current && !sections.has(key)) sections.set(key, { base: baseSection, current });
      });
    });
  return { fields, sections };
}

export function RegistryFormScope({ moduleKey, submoduleKey, children }) {
  const [forms] = useFormRegistryCollection();
  const index = useMemo(() => buildIndex(forms, moduleKey, submoduleKey), [forms, moduleKey, submoduleKey]);
  return <ScopeContext.Provider value={index}>{children}</ScopeContext.Provider>;
}

function changed(match, key) {
  return JSON.stringify(match.base[key] ?? null) !== JSON.stringify(match.current[key] ?? null);
}

function optionLabel(option) {
  if (option === null || option === undefined) return '';
  if (typeof option !== 'object') return String(option);
  return String(option.label ?? option.name ?? option.title ?? option.value ?? '');
}

// Rebuilds the page's option list from the admin's edited list, reusing the
// page's own option object (and so its submitted value/code) wherever the
// labels match; options the admin added are submitted as their label.
// Options loaded from a reference table (source "reference:…") are left
// alone — the registry only holds placeholders for those.
export function applyRegistryOptions(match, options) {
  if (!match || !Array.isArray(options) || !Array.isArray(match.current.options)) return options;
  if (String(match.current.source ?? '').startsWith('reference:') || !changed(match, 'options')) return options;

  const baseLabels = new Set((match.base.options ?? []).map((option) => normalizeRegistryLabel(optionLabel(option))));
  const pageByLabel = new Map(options.map((option) => [normalizeRegistryLabel(optionLabel(option)), option]));
  const usesObjects = options.some((option) => option && typeof option === 'object');

  const edited = match.current.options.map((option) => {
    const label = optionLabel(option);
    const pageOption = pageByLabel.get(normalizeRegistryLabel(label));
    if (pageOption !== undefined) return pageOption;
    return usesObjects ? { value: label, label } : label;
  });
  const editedLabels = new Set(edited.map((option) => normalizeRegistryLabel(optionLabel(option))));
  // Page options the registry never knew about (e.g. added by code later)
  // stay; ones the admin removed from the default list are dropped.
  const extras = options.filter((option) => {
    const key = normalizeRegistryLabel(optionLabel(option));
    return !baseLabels.has(key) && !editedLabels.has(key);
  });
  return [...edited, ...extras];
}

export function useRegistryMatch(label) {
  const index = useContext(ScopeContext);
  if (!index) return null;
  return index.fields.get(normalizeRegistryLabel(label)) ?? null;
}

// Returns the props a field should render with. Only props the admin actually
// changed in Form Registry are replaced.
export function useRegistryFieldProps(label, props = {}) {
  const match = useRegistryMatch(label);
  if (!match) return { ...props, label, match: null };
  const { current } = match;
  // Keep a trailing "*" if the page wrote its required marker into the label.
  const starred = /\*\s*$/.test(String(label ?? ''));
  return {
    ...props,
    label: changed(match, 'label') ? `${current.label}${starred ? ' *' : ''}` : label,
    required: changed(match, 'required') ? Boolean(current.required) : props.required,
    hint: changed(match, 'hint') ? current.hint : props.hint,
    placeholder: changed(match, 'placeholder') && current.placeholder ? current.placeholder : props.placeholder,
    options: applyRegistryOptions(match, props.options),
    match,
  };
}

// A label alone (e.g. a review-step row) — the edited label, if renamed.
export function useRegistryLabel(label) {
  return useRegistryFieldProps(label).label;
}

// Lets a select nested inside a Field (which only the Field knows the label
// of) pick up that field's edited options.
export function RegistryFieldBoundary({ match, children }) {
  return <FieldContext.Provider value={match}>{children}</FieldContext.Provider>;
}

export function useRegistryOptions(options) {
  const match = useContext(FieldContext);
  return applyRegistryOptions(match, options);
}

export function useRegistryPlaceholder(placeholder) {
  const match = useContext(FieldContext);
  if (!match || !changed(match, 'placeholder') || !match.current.placeholder) return placeholder;
  return match.current.placeholder;
}

export function useRegistrySectionTitle(title) {
  const index = useContext(ScopeContext);
  const match = index?.sections.get(normalizeRegistryLabel(title));
  if (!match || match.base.title === match.current.title) return title;
  return match.current.title;
}
